import React, { useCallback, useEffect, useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	ListItem,
	Skeleton,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import GroupsIcon from "@mui/icons-material/Groups";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PieChartIcon from "@mui/icons-material/PieChart";
import DownloadIcon from "@mui/icons-material/Download";

import DefaultTable from "../../../components/Table/DefaultTable";
import { useCompanyData } from "../../../hook/useCompanyData";
import { getQuoteSearchAll } from "../../../apis/quote";
import { getOptionData1, subTotal } from "../../../utils/quote";
import { Model } from "../../../utils/model";
import { useModelData } from "../../../hook/useModelData";
import { useOptionData } from "../../../hook/useOptionData";
import { ModelOptionDataType } from "../../../types/global.types";
import { RootState } from "../../../store/store";

type DateRange = "30days" | "lastMonth" | "yearToDate" | "allTime";

const dateRangeOptions: { value: DateRange; label: string }[] = [
	{ value: "30days", label: "Last 30 Days" },
	{ value: "lastMonth", label: "Last Month" },
	{ value: "yearToDate", label: "Year to Date" },
	{ value: "allTime", label: "All Time" },
];

interface KPICardProps {
	title: string;
	value: string | number;
	icon: React.ReactNode;
	color: string;
	loading?: boolean;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, icon, color, loading }) => (
	<Box sx={{ flex: "1 1 220px", minWidth: 200 }}>
		<Card
			sx={{
				height: "100%",
				borderLeft: `4px solid ${color}`,
				transition: "box-shadow 0.2s ease",
				"&:hover": {
					boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
				},
			}}
		>
			<CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Box>
						{loading ? (
							<>
								<Skeleton width={80} height={40} />
								<Skeleton width={100} height={20} />
							</>
						) : (
							<>
								<Typography
									variant="h1"
									sx={{
										fontSize: "1.75rem",
										fontWeight: 700,
										color: "text.primary",
										lineHeight: 1.2,
									}}
								>
									{value}
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									{title}
								</Typography>
							</>
						)}
					</Box>
					<Box
						sx={{
							width: 48,
							height: 48,
							borderRadius: 2,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							bgcolor: `${color}14`,
							color: color,
							flexShrink: 0,
						}}
					>
						{icon}
					</Box>
				</Stack>
			</CardContent>
		</Card>
	</Box>
);

const statusChipColor = (status: string): { bg: string; color: string } => {
	const s = status?.toLowerCase();
	if (s === "sold") return { bg: "#ECFDF5", color: "#059669" };
	if (s === "expired") return { bg: "#FEF2F2", color: "#DC2626" };
	return { bg: "#EFF6FF", color: "#2563EB" };
};

const headerData: string[] = [
	`${process.env.REACT_APP_DEALER_FULL_NAME}`,
	"Creator",
	"Contact",
	"Opt. Reference",
	"Quote #",
	"Date",
	"Status",
	"Preferred Price",
	"MSRP",
];

export const Dashboard: React.FC = () => {
	const currentAdmin = useSelector((state: RootState) => state.adminAuth.admin);
	const [snapshotTerm, setSnapshotTerm] = useState<DateRange>("30days");
	const [page, setPage] = useState<number>(0);
	const [pageSize, setPageSize] = useState<number>(20);
	const [count, setCount] = useState<number>(0);
	const [customerNumber, setCustomerNumber] = useState<number>(0);
	const [salesNumber, setSalesNumber] = useState<number>(0);
	const [closePercentage, setClosePercentage] = useState<number>(0);
	const [data, setData] = useState<any>();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [quotes, setQuotes] = useState([]);

	const { company } = useCompanyData();
	const { models } = useModelData();
	const { options } = useOptionData();

	const priceCalculation = (data: any, type: string) => {
		const modelOptionData = data?.model_option_ids
			? JSON.parse(data?.model_option_ids)
			: [];

		return modelOptionData?.map((res: ModelOptionDataType) => {
			let _model = models.find(
				(model: Model) => model.ID === res.model_id?.ID
			);

			return {
				model: {
					model_name: _model?.model_name,
					part_number: _model?.part_number,
					dealer_price: res.model_id?.[type],
					MSRP: res.model_id?.MSRP,
				},
				options: getOptionData1(options, res.option_ids),
				qty: res.qty,
			};
		});
	};

	const fetchQuotes = async () => {
		try {
			const {
				data: quoteData,
				counts,
				customerNumber,
				salesNumber,
				closePercentage,
				allData,
			} = await getQuoteSearchAll({
				page: page + 1,
				pageSize,
				dateRange: snapshotTerm || "30days",
			});

			const transformedData = quoteData?.map((quote: any, idx: number) => {
				const shouldTruncate = quote.opt_reference?.length > 50;
				const truncatedReference = shouldTruncate
					? `${quote.opt_reference.slice(0, 50)}...`
					: quote.opt_reference;

				const optReferenceElement = shouldTruncate ? (
					<Tooltip key={`optRef-${idx}`} title={quote.opt_reference}>
						<span>{truncatedReference}</span>
					</Tooltip>
				) : (
					<span>{truncatedReference}</span>
				);

				const { bg, color } = statusChipColor(quote.quote_status);

				return {
					dealer:
						quote.dealer_id === "ADMIN" ? (
							company?.company_name
						) : (
							<ListItem
								disablePadding
								component={Link}
								to={`/dealers/${quote.dealerId}`}
							>
								{quote.dealer_name}
							</ListItem>
						),
					creator:
						quote.dealer_id === "ADMIN"
							? quote?.adminCreator || "N/A"
							: quote?.dealerCreator,
					contact:
						quote.dealer_id === "ADMIN"
							? company?.contact_name
							: quote?.contact_name,
					opt_reference: optReferenceElement,
					quotes: (
						<ListItem
							disablePadding
							component={Link}
							to={`/quote/${quote.quoteId}`}
						>
							{quote.no}
						</ListItem>
					),
					last_activity: moment(quote.createdAt).format("MM/DD/YYYY"),
					status: (
						<Chip
							label={quote.quote_status}
							size="small"
							sx={{
								bgcolor: bg,
								color: color,
								fontWeight: 600,
								fontSize: "0.75rem",
							}}
						/>
					),
					dealer_price: `$${subTotal(priceCalculation(quote, "dealer_price"), company, "dealer_price")?.toFixed(2)}`,
					msrp: `$${subTotal(priceCalculation(quote, "MSRP"), company, "MSRP")?.toFixed(2)}`,
				};
			});

			setData(allData);
			setQuotes(transformedData);
			setCount(counts);
			setCustomerNumber(customerNumber);
			setSalesNumber(salesNumber);
			setClosePercentage(closePercentage || 0);
			setIsLoading(false);
		} catch (error) {
			console.error("Failed to fetch admins", error);
			setIsLoading(false);
		}
	};

	useEffect(() => {
		setIsLoading(true);
		fetchQuotes();
	}, [company, page, pageSize, snapshotTerm]);

	const handleDateRangeChange = (_: React.MouseEvent<HTMLElement>, newValue: DateRange | null) => {
		if (newValue !== null) {
			setPage(0);
			setSnapshotTerm(newValue);
		}
	};

	const handleChangePage = (page: number) => {
		setPage(page);
	};

	const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setPageSize(Number(e.target.value));
	};

	const priceValue = useCallback(
		(index: number, type: string) => {
			const modelOptionData = data?.[index]?.model_option_ids
				? JSON.parse(data?.[index]?.model_option_ids)
				: [];

			return modelOptionData?.map((res: ModelOptionDataType) => {
				let _model = models.find(
					(model: Model) => model.ID === res.model_id?.ID
				);

				return {
					model: {
						model_name: _model?.model_name,
						part_number: _model?.part_number,
						dealer_price: res.model_id?.[type],
						MSRP: res.model_id?.MSRP,
					},
					options: getOptionData1(options, res.option_ids),
					qty: res.qty,
				};
			});
		},
		[data, models, options]
	);

	const quoteExpirationDate = (quote: any) => {
		if (!company?.createdAt) return "";

		let date = new Date(quote?.createdAt);

		const expirationNumber =
			Number(company?.quote_expiration_setting_number) || 0;

		if (company?.quote_expiration_setting_date === "month") {
			date.setMonth(date.getMonth() + expirationNumber);
		} else if (company?.quote_expiration_setting_date === "year") {
			date.setFullYear(date.getFullYear() + expirationNumber);
		} else {
			date.setDate(date.getDate() + expirationNumber);
		}
		return moment(date).format("MM/DD/YYYY");
	};

	const handleDownloadCSV = () => {
		const headers = headerData.join(",") + ", Expiration Date" + "\n";

		const escapeCSV = (value: string | number | null | undefined): string => {
			if (value === null || value === undefined) return "";
			const str = String(value).replace(/"/g, '""');
			return `"${str}"`;
		};

		const csvRows = data?.map((quote: any, index: number) => {
			const dealer =
				quote.dealer_id === "ADMIN"
					? company?.company_name
					: quote.dealer_name;
			const creator =
				quote.dealer_id === "ADMIN"
					? quote?.adminCreator || "N/A"
					: quote?.dealerCreator;
			const contact =
				quote.dealer_id === "ADMIN"
					? company?.contact_name
					: quote?.contact_name;
			const optRef = quote.opt_reference;
			const quoteNo = quote.no;
			const createdAt = moment(quote.createdAt).format("MM/DD/YYYY");
			const status = quote.quote_status;
			const dealerPrice = subTotal(
				priceValue(index, "dealer_price"),
				company,
				"dealer_price"
			)?.toFixed(2);
			const msrp = subTotal(
				priceValue(index, "MSRP"),
				company,
				"MSRP"
			)?.toFixed(2);
			const expiration = quoteExpirationDate(quote);

			return [
				dealer,
				creator,
				contact,
				optRef,
				quoteNo,
				createdAt,
				status,
				dealerPrice,
				msrp,
				expiration,
			]
				.map(escapeCSV)
				.join(",");
		});

		const csvString = headers + csvRows.join("\n");

		const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.setAttribute("download", "table.csv");
		document.body.appendChild(link);
		link.click();
		URL.revokeObjectURL(link.href);
		document.body.removeChild(link);
	};

	return (
		<Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
			{/* Welcome Section */}
			<Box sx={{ mb: 4 }}>
				<Typography
					variant="h1"
					sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 700 }}
				>
					Welcome back, {currentAdmin?.first_name || "Admin"}
				</Typography>
				<Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
					Here's what's happening with your dealer portal today.
				</Typography>
			</Box>

			{/* Date Range Filter */}
			<Box sx={{ mb: 3 }}>
				<ToggleButtonGroup
					value={snapshotTerm}
					exclusive
					onChange={handleDateRangeChange}
					size="small"
					sx={{
						"& .MuiToggleButton-root": {
							textTransform: "none",
							fontWeight: 500,
							px: 2,
							py: 0.75,
							borderColor: "divider",
							"&.Mui-selected": {
								bgcolor: "primary.main",
								color: "primary.contrastText",
								"&:hover": {
									bgcolor: "primary.dark",
								},
							},
						},
					}}
				>
					{dateRangeOptions.map((opt) => (
						<ToggleButton key={opt.value} value={opt.value}>
							{opt.label}
						</ToggleButton>
					))}
				</ToggleButtonGroup>
			</Box>

			{/* KPI Cards */}
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					gap: 2.5,
					mb: 4,
				}}
			>
				<KPICard
					title="Total Quotes"
					value={isLoading ? "" : count}
					icon={<RequestQuoteIcon sx={{ fontSize: 28 }} />}
					color="#3B82F6"
					loading={isLoading}
				/>
				<KPICard
					title="Total Customers"
					value={isLoading ? "" : customerNumber}
					icon={<GroupsIcon sx={{ fontSize: 28 }} />}
					color="#10B981"
					loading={isLoading}
				/>
				<KPICard
					title="Total Sales"
					value={isLoading ? "" : salesNumber}
					icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
					color="#F59E0B"
					loading={isLoading}
				/>
				<KPICard
					title="Close Rate"
					value={isLoading ? "" : `${closePercentage?.toFixed(2)}%`}
					icon={<PieChartIcon sx={{ fontSize: 28 }} />}
					color="#00B4D8"
					loading={isLoading}
				/>
			</Box>

			{/* Recent Quotes Section */}
			<Card sx={{ overflow: "visible" }}>
				<CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: 3 } }}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						justifyContent="space-between"
						alignItems={{ xs: "flex-start", sm: "center" }}
						sx={{ mb: 2.5 }}
						spacing={1.5}
					>
						<Typography variant="h3" sx={{ fontWeight: 600 }}>
							Recent Quotes
						</Typography>
						<Button
							variant="outlined"
							size="small"
							startIcon={<DownloadIcon />}
							onClick={handleDownloadCSV}
							disabled={isLoading || !quotes?.length}
							sx={{ textTransform: "none" }}
						>
							Download CSV
						</Button>
					</Stack>

					{isLoading ? (
						<Stack spacing={1}>
							{[...Array(5)].map((_, i) => (
								<Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
							))}
						</Stack>
					) : (
						<DefaultTable
							headerData={headerData}
							rowsData={quotes}
							rowsPerPage={pageSize}
							page={page}
							count={count}
							handleChangePage={(
								e: React.ChangeEvent<HTMLInputElement>,
								page: number
							) => handleChangePage(page)}
							handleChangeRowsPerPage={(
								e: React.ChangeEvent<HTMLInputElement>
							) => handleChangeRowsPerPage(e)}
						/>
					)}
				</CardContent>
			</Card>
		</Box>
	);
};
